<template>
	<div
		class="unsplash"
		data-cy="unsplash"
	>
		<helio-field
			:placeholder="$t('upload.unsplash.placeholder')"
			:display-success="false"
			:initial-value="lastSearchTerm"
			icon="search"
			icon-weight="light"
			data-cy="unsplash-field"
			@changed="searchFor($event)"
		/>

		<div
			v-if="loading"
			class="unsplash__loading"
		>
			<helio-spinner
				color="blue"
			/>
		</div>

		<div
			v-if="isEmpty"
			class="unsplash__empty"
		>
			<helio-icon
				token="images"
				weight="light"
			/>
		</div>

		<div
			v-if="isNoResults"
			class="unsplash__no-results"
		>
			<helio-empty
				:initial-title="$t('upload.unsplash.empty.title', [searchTerm])"
				:initial-subtitle="$t('upload.unsplash.empty.subtitle', [searchTerm])"
				illustration="feather"
			/>
		</div>

		<div
			v-if="searchResults.length > 0 && !loading"
			ref="grid"
			:class="{
				'unsplash__grid--loading' : imageLoading,
			}"
			class="unsplash__grid"
		>
			<unsplash-item
				v-for="(item, index) in searchResults"
				:key="index"
				:item="item"
				data-cy="unsplash-item"
				@selected="shuffleGrid"
			/>
		</div>
	</div>
</template>

<script>
import _debounce from 'lodash/debounce';
// eslint-disable-next-line
import Unsplash, { toJson } from 'unsplash-js';
import Field from '../../field/Field.vue';
import Icon from '../../icon/Icon.vue';
import Empty from '../../empty/Empty.vue';
import Spinner from '../../spinner/Spinner.vue';
import UnsplashItem from './UnsplashItem.vue';

let Masonry = null;

if (!process.server) {
	// eslint-disable-next-line
	Masonry = require('masonry-layout');
}

export default {
	components: {
		'helio-field': Field,
		'helio-icon': Icon,
		'helio-empty': Empty,
		'helio-spinner': Spinner,
		'unsplash-item': UnsplashItem,
	},

	props: {
		appId: {
			type: String,
			required: true,
		},

		secret: {
			type: String,
			required: true,
		},

		lastSearchTerm: {
			type: String,
			required: false,
			default: null,
		},
	},

	data() {
		return {
			searchTerm: null,
			searchResults: [],
			page: 1,
			unsplash: null,
			loading: false,
			imageLoading: false,
			masonry: null,
			loadingMore: false,
			imagePerPage: 30,
		};
	},

	computed: {
		isEmpty() {
			return this.searchTerm === null && this.searchResults.length === 0 && !this.loading;
		},
		isNoResults() {
			return this.searchTerm !== null && this.searchResults.length === 0 && !this.loading;
		},
	},

	beforeCreate() {
		this.$root.$off('add-media-in-gallery');
	},

	created() {
		this.unsplash = new Unsplash({
			applicationId: this.appId,
			secret: this.secret,
		});

		this.$root.$on('add-media-in-gallery', (url) => {
			this.searchResults = this.searchResults.filter(result => result.thumbnail !== url);
			this.$forceUpdate();
		});
	},

	beforeDestroy() {
		if (this.$refs.grid != null) {
			this.$refs.grid.removeEventListener('scroll', this.handleGridScroll);
		}
	},

	methods: {
		/* eslint-disable-next-line */
		searchFor: _debounce(function (term) {
			this.searchResults = [];
			this.searchTerm = null;
			if (this.unsplash && term.length > 2) {
				this.loading = true;
				this.searchTerm = term;
				this.page = 1;
				this.$emit('changed-search', this.searchTerm);

				this.unsplash.search.photos(this.searchTerm, this.page, this.imagePerPage)
					.then(toJson)
					.then((json) => {
						const results = (json.results) ? json.results : [];
						this.handleResults(this.searchTerm, results);
					});
			}
		}, 250),

		handleResults(term, results, imageLoading = true) {
			results.forEach((result) => {
				this.searchResults.push({
					name: term,
					full: result.urls.full,
					regular: result.urls.regular,
					thumbnail: result.urls.thumb,
					width: result.width,
					height: result.height,
					type: 'unsplash',
					author: result.user.name,
					website: result.user.links.html,
					download: result,
				});
			});
			this.shuffleGrid();
			this.loading = false;
			this.imageLoading = imageLoading;
		},

		handleGridScroll() {
			const { grid } = this.$refs;
			const maxScroll = grid.scrollHeight - grid.clientHeight;
			const deltaScroll = maxScroll - grid.scrollTop;

			const threshold = 250;
			if (deltaScroll < threshold && !this.loadingMore) {
				this.page += 1;
				this.loadingMore = true;
				this.unsplash.search.photos(this.searchTerm, this.page, this.imagePerPage)
					.then(toJson)
					.then((json) => {
						const results = (json.results) ? json.results : [];
						this.handleResults(this.searchTerm, results, false);
						this.loadingMore = false;
					});
			}
		},

		shuffleGrid(item) {
			if (item !== undefined && item.download) {
				this.unsplash.photos.downloadPhoto(item.download);
			}

			setTimeout(() => {
				if (this.$refs.grid) {
					this.$refs.grid.removeEventListener('scroll', this.handleGridScroll);
					this.$refs.grid.addEventListener('scroll', this.handleGridScroll);

					if (!process.server) {
						this.masonry = new Masonry(this.$refs.grid, {
							gutter: 20,
							percentPosition: true,
							itemSelector: '.unsplash-item',
							columnWidth: '.unsplash-item',
						});
					}

					this.imageLoading = false;
				}
			}, 500);
		},
	},
};
</script>

<style lang="scss" src="./unsplash.scss" />
